import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  HotelSlotProvider,
  useDefaultHotelSlots,
} from "@b2bportal/core-hotels";
import { CoreHotelsComponent } from "@b2bportal/core-types";
import type { Lodging } from "@b2bportal/lodging-api";
import { HotelListOverrideContext } from "@tenant/hotels";
import { PackageHotelAvailabilitySearch } from "../HotelAvailabilitySearch";
import { HotelCardPrice } from "../HotelCardPrice";
import { HotelListError } from "../HotelListError";
import { PackageHotelMapMarkers } from "../HotelMapMarkers";
import { PackageHotelRoomCardRates } from "../HotelRoomCardRates";
import { PackageHotelRoomSelection } from "../HotelRoomSelection";
import { PackageHotelShopBookingSection } from "../HotelShopBookingSection";
import { PackageBreadcrumbs } from "../PackageBreadcrumbs";
import { PackageHotelFilterDialog } from "../PackageHotelFilterDialog";
import { PackageHotelShopPriceSummary } from "@/pages/Packages/pages/PackageHotelShopPage/PackageHotelShopPriceSummary";
import { PackageHotelSelectors } from "@/store/packages";

//TODO(Nate): Add the rest of the slots once basic e2e flow is working
export const PackagesHotelSlotProvider = ({ children }) => {
  const defaultSlots = useDefaultHotelSlots();
  const packageStateByLodgingId = useSelector(
    PackageHotelSelectors.getPackageStateByLodgingId
  );

  const sortComparator = useCallback(
    (a: Lodging, b: Lodging) => {
      const aPrice =
        packageStateByLodgingId?.[a.lodging.id]?.price?.totalPrice?.fiat?.value;
      const bPrice =
        packageStateByLodgingId?.[b.lodging.id]?.price?.totalPrice?.fiat?.value;

      if (aPrice == null && bPrice == null) return 0;
      if (aPrice == null) return 1;
      if (bPrice == null) return -1;
      return aPrice - bPrice;
    },
    [packageStateByLodgingId]
  );

  const priceExtractor = useCallback(
    (lodging: Lodging) =>
      packageStateByLodgingId?.[lodging.lodging.id]?.price?.totalPrice?.fiat
        ?.value ?? null,
    [packageStateByLodgingId]
  );

  const overrideValue = useMemo(
    () =>
      packageStateByLodgingId ? { sortComparator, priceExtractor } : undefined,
    [packageStateByLodgingId, sortComparator, priceExtractor]
  );

  const slotComponents =
    defaultSlots != null
      ? {
          ...defaultSlots,
          [CoreHotelsComponent.HotelCardPrice]: HotelCardPrice,
          [CoreHotelsComponent.HotelBreadcrumbs]: PackageBreadcrumbs,
          [CoreHotelsComponent.HotelListError]: HotelListError,
          [CoreHotelsComponent.HotelAvailabilitySearch]:
            PackageHotelAvailabilitySearch,
          [CoreHotelsComponent.HotelShopBookingSection]:
            PackageHotelShopBookingSection,
          [CoreHotelsComponent.HotelRoomCardRates]: PackageHotelRoomCardRates,
          [CoreHotelsComponent.HotelRoomSelection]: PackageHotelRoomSelection,
          [CoreHotelsComponent.HotelMapMarkers]: PackageHotelMapMarkers,
          [CoreHotelsComponent.HotelMapRedoSearchButton]: null,
          [CoreHotelsComponent.HotelShopHeader]: PackageHotelShopPriceSummary,
          [CoreHotelsComponent.FilterDialog]: PackageHotelFilterDialog,
        }
      : null;

  if (slotComponents == null) {
    return null;
  }

  return (
    <HotelListOverrideContext.Provider value={overrideValue}>
      <HotelSlotProvider
        context={{
          components: slotComponents,
          providerName: "packages-hotels",
        }}
      >
        {children}
      </HotelSlotProvider>
    </HotelListOverrideContext.Provider>
  );
};
